import { useLineItemApiServicePutApiLineItemApiLineItemProperties } from "../../../../openapi/api/queries/queries";
import { useCadStatus } from "../../customContext/CadStatusContext";

import { JSX, useCallback } from "react";
import { useTranslation } from "react-i18next";

import {
  VMConfigurations,
  VMLineItems,
  useCompaniesServiceGetAllCompanies,
  useConfigurationsServiceCreateConfigurations,
  useConfigurationsServiceGetByIdConfigurations,
  useConfigurationsServiceUpdateConfigurations,
  useLineItemsServiceCreateLineItems,
  useStatus,
} from "@4pace-cpq/core";
import Button from "@mui/material/Button";
import {
  useConfigurationApi,
  useCsConfigurationApi,
  useSessionId,
} from "@plansoft-configuration-renderer/framework";
import { useNavigate, useSearch } from "@tanstack/react-router";

export function SaveConfigurationButton({
  configurationId,
  label,
  callback,
  fullWidth,
}: {
  configurationId: VMConfigurations["id"];
  label?: string;
  callback?: () => void;
  fullWidth?: boolean;
}): JSX.Element {
  const { itemGroupId, lineItemId } = useSearch({
    strict: false,
  });
  const { isCadLoading } = useCadStatus();
  const navigate = useNavigate();
  const { data: companies } = useCompaniesServiceGetAllCompanies();
  const { mutate: createLineItem } = useLineItemsServiceCreateLineItems();
  const { mutate: createConfiguration } =
    useConfigurationsServiceCreateConfigurations();
  const { mutate: setLineItemProperties } =
    useLineItemApiServicePutApiLineItemApiLineItemProperties();
  const sessionId = useSessionId();
  const api = useConfigurationApi();

  const { status } = useStatus();

  const { t } = useTranslation();
  const { saveConfiguration } = useCsConfigurationApi();

  const { data: configuration } = useConfigurationsServiceGetByIdConfigurations(
    { id: configurationId },
  );

  const { mutate: updateConfiguration } =
    useConfigurationsServiceUpdateConfigurations();

  const handleUpdateLineItemProperties = useCallback(
    async (lineItemId: string): Promise<string> => {
      const res = await api.invokeUserDefinedGetter({
        id: sessionId,
        name: "getProjectListInfo",
        invokeUserDefinedGetterParams: {
          path: "/XAS_PRODUCT_VIEW/0/",
          input: JSON.stringify({}),
        },
      });
      if (res?.output) {
        const data = JSON.parse(res?.output);
        console.log("getProjectListInfo", data);

        setLineItemProperties({
          lineItemId: lineItemId,
          requestBody: {
            typeCode: data.typeCode,
            priceIndicator: data.priceIndicator
              ? data.priceIndicator.toString()
              : "0",
          },
        });
        return res?.output;
      }
      return "";
    },
    [api, sessionId, setLineItemProperties],
  );

  const handleCreateConfiguration = useCallback(
    async (lineItemId: VMLineItems["id"]): Promise<void> => {
      const { state } = await saveConfiguration();
      createConfiguration(
        {
          requestBody: {
            //todo remove if BE allows to remove
            identifier: "-",
            lineItemId,
            state,
          },
        },
        {
          onSuccess: async (configuration) => {
            await handleUpdateLineItemProperties(lineItemId);
            if (typeof callback === "function") {
              callback(); //Callback normally navigates to another page.
            } else {
              navigate({
                to: "/configurator",
                replace: true,
                search: (prevSearch: object) => {
                  return {
                    ...prevSearch,
                    configurationId: configuration.id,
                    lineItemId,
                    dummy: "test",
                  };
                },
              });
            }
          },
          onError: (error) => {
            console.error("Error creating configuration", error);
          },
        },
      );
    },
    [
      createConfiguration,
      navigate,
      saveConfiguration,
      handleUpdateLineItemProperties,
      callback,
    ],
  );

  const handleCreateLineItem = useCallback(async () => {
    if (itemGroupId && companies && status) {
      await createLineItem(
        {
          requestBody: {
            companyId: companies[0]?.id || "",
            statusId: status.default.id || "",
            itemGroupId,
          },
        },
        {
          onSuccess: async (lineItem) => {
            await handleCreateConfiguration(lineItem.id);
          },
          onError: (error) => {
            console.error("Error creating line item", error);
          },
        },
      );
    }
  }, [
    companies,
    createLineItem,
    handleCreateConfiguration,
    status,
    itemGroupId,
  ]);

  async function handleClick(): Promise<void> {
    if (!lineItemId) {
      await handleCreateLineItem();
    } else {
      const { state } = await saveConfiguration();

      if (configurationId && configuration && configuration.lineItemId) {
        updateConfiguration(
          {
            id: configurationId,
            requestBody: {
              rowVersion: configuration.rowVersion,
              state,
              lineItemId: configuration.lineItemId,
            },
          },
          {
            onError: (error) => {
              console.error("Error creating configuration", error);
            },
          },
        );

        await handleUpdateLineItemProperties(configuration.lineItemId);
      } else {
        console.error("Error creating configuration", { configurationId });
      }
      if (typeof callback === "function") {
        callback(); //Callback normally navigates to another page.
      }
    }
  }

  return (
    <Button
      variant="contained"
      color="primary"
      onClick={handleClick}
      disabled={isCadLoading}
      loadingPosition="start"
      loading={isCadLoading}
      // disabled={!configuration}
      fullWidth={fullWidth}
    >
      {label ? label : t("WEG:buttons.SaveConfigurationButton")}
    </Button>
  );
}
